import React from 'react'
import { usePage } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import PipelineVisualizer from '@/components/help/widgets/PipelineVisualizer'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function HowItWorks({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND
  // Room-Type Demand is globally kill-switched (App::RateEngine::Config); when off it is hidden
  // from the pipeline description and the stage count drops accordingly.
  const roomDemandEnabled = usePage().props.roomDemandEnabled === true
  const stageCount = roomDemandEnabled ? 13 : 12

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title={`How ${brand} Works`}
      subtitle="Understand how rates flow from your rules to your PMS — and why every rate is fully traceable."
    >
      <GuideSection title={`What is ${brand}?`}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {brand} is an <strong className="text-foreground">automated revenue management system</strong>.
          It connects to your PMS, watches your bookings, and adjusts rates every hour based
          on rules you control. The goal is simple: charge the right price for every room, every night,
          without manual guesswork.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {brand} is <strong className="text-foreground">not</strong> a PMS. Your PMS remains in
          charge of bookings, guests, and availability. {brand} only manages
          rates — it reads booking data from your PMS, computes optimised prices, and pushes them back.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Three principles guide how it works:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Deterministic</strong> — the same inputs always produce
            the same output. There is no randomness or hidden logic. If you run the pipeline twice with
            the same data, you get the same rates.
          </li>
          <li>
            <strong className="text-foreground">Transparent</strong> — every rate is fully traceable.
            You can hover over any calendar cell and see exactly which stages adjusted the price and by
            how much. Nothing is a black box.
          </li>
          <li>
            <strong className="text-foreground">Operator-controlled</strong> — you set the rules, the
            engine executes. {brand} will never change a rate in a way that does not trace back to a
            rule you configured.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title={`The ${stageCount}-Stage Rate Pipeline`}>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Every rate passes through <strong className="text-foreground">{stageCount} stages</strong> in a fixed
          order. Each stage either adjusts the rate or passes it through unchanged. The pipeline runs
          the same way every time — no shortcuts, no skipped steps. Every room type runs through the
          full pipeline independently.
        </p>
        <ol className="ml-4 list-decimal space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Base Rate</strong> — your starting price per room type
            and occupancy level. This is the anchor for everything else.
          </li>
          <li>
            <strong className="text-foreground">Seasonal</strong> — date-based percentage adjustments
            for predictable demand patterns (summer peak, Christmas, low season).
          </li>
          <li>
            <strong className="text-foreground">Day of Week</strong> — percentage adjustments for
            specific days (Friday premium, midweek discount).
          </li>
          <li>
            <strong className="text-foreground">Property Demand</strong> — dynamic adjustment based on
            how fast bookings are coming in relative to your historical baseline across the whole
            property. Includes lead time amplification — reacts more decisively as check-in approaches.
          </li>
          <li>
            <strong className="text-foreground">Target Pressure</strong> — steers rates toward the
            monthly targets you commit to. When your projected end-of-month occupancy sits below
            target, it pushes harder to stimulate bookings; when you are ahead, it eases off to protect
            rate. Only active for months where you have saved a target, and it stands down whenever the
            projection is not yet confident enough to act on.
          </li>
          {roomDemandEnabled && (
            <li>
              <strong className="text-foreground">Room Demand</strong> — fine-tunes pricing for
              individual room types that are booking faster or slower than the property average.
            </li>
          )}
          <li>
            <strong className="text-foreground">Event</strong> — boosts for local events like
            festivals, conferences, and sports fixtures.
          </li>
          <li>
            <strong className="text-foreground">Market Position</strong> — compares your rate to your
            competitive set and nudges it within your bounds: up when you're cheaper than the market,
            down when you're pricier, and nothing at all when you're already about right.
          </li>
          <li>
            <strong className="text-foreground">Orphan Day</strong> — fills single-night gaps between
            bookings to avoid unsellable inventory.
          </li>
          <li>
            <strong className="text-foreground">Clamp</strong> — enforces your floor and ceiling price
            limits. No rate goes below the minimum or above the maximum you set.
          </li>
          <li>
            <strong className="text-foreground">Rounding</strong> — rounds to the nearest increment
            (typically £1). Guests never see fractional pennies.
          </li>
          <li>
            <strong className="text-foreground">Override</strong> — replaces the computed rate with
            your manual override, if you have set one for that date.
          </li>
          <li>
            <strong className="text-foreground">Restrictions</strong> — applies stop sell, closed to
            arrival, closed to departure, and min/max stay rules.
          </li>
        </ol>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Rate Plan Offset:</strong> After the {stageCount} stages,
          non-lead rate plans get their per-plan offsets (e.g., Non-Refundable = Lead Rate − 10%), and
          rate-stability caps smooth the final change.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Rate Stability:</strong> After the pipeline runs, the
          engine checks whether the rate changed too sharply from the previous computation. Large
          decreases are dampened and large increases are capped, so guests and OTAs see smooth,
          predictable rate movements rather than sudden jumps.
        </p>

        <GuideWidget
          title="Pipeline Visualizer"
          footnote="This uses the same stage order and math as the actual rate engine."
        >
          <PipelineVisualizer />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="The Compute Cycle">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Rates are <strong className="text-foreground">checked and updated every hour</strong>,
          automatically. Each hour {brand} pulls the latest booking data from your PMS, then updates
          the prices for the dates that booking activity has affected — so a new booking reaches your
          rates within the hour. A fuller review of every date on the calendar runs every couple of
          hours, so nothing drifts out of date.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Rates are also <strong className="text-foreground">recomputed immediately</strong> when you
          change pricing rules or set overrides. You do not need to wait for the next scheduled cycle —
          the effect is visible in the calendar within minutes.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          After computation, the final rates are{' '}
          <strong className="text-foreground">pushed to your PMS</strong>. The
          pipeline status strip in the top bar shows the current state:{' '}
          <strong className="text-foreground">inbound sync</strong> (pulling booking data) →{' '}
          <strong className="text-foreground">computation</strong> (running the 13 stages) →{' '}
          <strong className="text-foreground">push</strong> (sending rates to your PMS).
        </p>
      </GuideSection>

      <GuideSection title="Key Concepts">
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Room Type</strong> — a category of room at your
            property (e.g., "Standard Double", "Sea View Suite"). Each room type has its own base rate
            and pricing rules.
          </li>
          <li>
            <strong className="text-foreground">Rate Plan</strong> — a pricing plan linked to a room
            type (e.g., "Best Available Rate", "Non-Refundable"). The lead rate plan runs the full
            pipeline; other plans can apply a fixed offset from it.
          </li>
          <li>
            <strong className="text-foreground">Occupancy</strong> — the number of guests staying in a
            room. Rates can differ by occupancy level (e.g., single vs. double occupancy).
          </li>
          <li>
            <strong className="text-foreground">ADR (Average Daily Rate)</strong> — total room revenue
            divided by rooms sold. This is the industry standard measure of revenue quality. A higher
            ADR means you are earning more per room sold.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            You do not need to configure every stage. Start with base rates and guardrails, then add
            complexity as you learn what works for your property. The pipeline handles unconfigured
            stages gracefully — they simply pass the rate through unchanged.
          </GuideTip>
          <GuideTip type="do">
            Every rate in the calendar is traceable. Hover over any cell to see the exact pipeline
            breakdown, or click to open the full day detail with per-stage running totals.
          </GuideTip>
          <GuideTip type="dont">
            Do not change multiple pricing rules at once. Change one thing, observe the effect in the
            calendar, then adjust further. This makes it much easier to understand cause and effect.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'rate-calendar', title: 'Rate Calendar' },
          { slug: 'base-rates', title: 'Base Rates' },
          { slug: 'demand-response', title: 'Demand Response' },
        ]}
      />
    </GuideLayout>
  )
}
